import { Button, Icon, Menu, MenuButton, MenuItem, MenuList, Tag } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCog, FaEdit, FaFileContract, FaFileSignature, FaPlus, FaSearch, FaTasks } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { requireBooleanEnvVar } from "../../../../config";
import { CreateContractModal } from "../../../../pages/contracts/create/CreateContractModal";
import { urls } from "../../../../urls";
import { useLoggedInWithOrgContextState } from "../../../auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../subscription/useSub";
import { Explanation } from "../../../support/Explanation/Explanation";

export const Contracts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const sub = useSub();

  const contractsTableEnabled = requireBooleanEnvVar("VITE_ENABLE_CONTRACTS_TABLE");

  const canCreateContract = authState.selectedOrg.canCreateContract;

  const contractsMessage = t("Get in touch via the chat in the bottom right corner to get access to contracts");

  const getNewContractDisabledMessage = (): string | null => {
    if (!canCreateContract) {
      return t("Only admins and creators can create new contracts");
    } else if (!sub.hasContractAccess) {
      return contractsMessage;
    }

    return null;
  };
  const newContractDisabledMessage = getNewContractDisabledMessage();

  const getContractTypesDisabledMessage = (): string | null => {
    if (!authState.selectedOrg.canEdit) {
      return t("Only admins can manage contract settings");
    } else if (!sub.hasContractAccess) {
      return contractsMessage;
    }

    return null;
  };
  const contractTypesDisabledMessage = getContractTypesDisabledMessage();

  const searchDisabledMessage = sub.canViewContracts
    ? null
    : t("Only members with contract access can search in contracts");

  return (
    <>
      {showCreateContractModal && <CreateContractModal onClose={() => setShowCreateContractModal(false)} />}
      <Menu>
        <MenuButton
          as={Button}
          leftIcon={<Icon as={FaFileSignature} w="15px" h="15px" />}
          colorScheme="teal"
          variant="ghost"
          size={"sm"}
        >
          {t("Contracts")}
        </MenuButton>
        <MenuList>
          <Explanation
            enabled={newContractDisabledMessage !== null}
            placement={"left-start"}
            text={newContractDisabledMessage}
          >
            <MenuItem
              isDisabled={newContractDisabledMessage !== null}
              icon={<Icon as={FaPlus} w="15px" h="15px" marginBottom={-1} />}
              onClick={() => setShowCreateContractModal(true)}
            >
              {t("New contract")}
            </MenuItem>
          </Explanation>
          <Explanation enabled={!sub.hasContractAccess} text={contractsMessage} placement="left">
            <MenuItem
              isDisabled={!sub.hasContractAccess}
              icon={<Icon as={FaEdit} w="15px" h="15px" marginBottom={-1} />}
              onClick={() => {
                navigate(urls.contracts.viewDrafts);
              }}
            >
              {t("Contract drafts")}
            </MenuItem>
          </Explanation>
          <MenuItem
            icon={<Icon as={FaFileContract} w="15px" h="15px" marginBottom={-1} />}
            onClick={() => {
              navigate(`${urls.contracts.viewPublished}?status=Active,Awarded`);
            }}
          >
            {t("All contracts")}
          </MenuItem>
          {sub.hasFencyContractSearchAccess && sub.fencySyncEnabled && (
            <Explanation enabled={searchDisabledMessage !== null} text={searchDisabledMessage} placement="left">
              <MenuItem
                isDisabled={searchDisabledMessage !== null}
                icon={<Icon as={FaSearch} w="15px" h="15px" marginBottom={-1} />}
                onClick={() => {
                  navigate(urls.contracts.search);
                }}
              >
                {t("Search in contracts")}
              </MenuItem>
            </Explanation>
          )}
          {sub.hasFencyContractSearchAccess && sub.fencySyncEnabled && (
            <Explanation enabled={searchDisabledMessage !== null} text={searchDisabledMessage} placement="left">
              <MenuItem
                isDisabled={searchDisabledMessage !== null}
                icon={<Icon as={FaSearch} w="15px" h="15px" marginBottom={-1} />}
                onClick={() => {
                  navigate(urls.contracts.searchV2);
                }}
              >
                {t("Search in contracts (chat)")}
              </MenuItem>
            </Explanation>
          )}
          {contractsTableEnabled && (
            <MenuItem
              icon={<Icon as={FaFileContract} w="15px" h="15px" marginBottom={-1} color="smPrimarySoft" />}
              onClick={() => {
                navigate(`${urls.contracts.viewAll}`);
              }}
            >
              {t("Contracts")} <Tag color="smPrimarySoft">BETA</Tag>
            </MenuItem>
          )}
          <Explanation
            enabled={contractTypesDisabledMessage !== null}
            text={contractTypesDisabledMessage}
            placement="left"
          >
            <MenuItem
              isDisabled={contractTypesDisabledMessage !== null}
              icon={<Icon as={FaCog} w="15px" h="15px" marginBottom={-1} />}
              onClick={() => {
                navigate(urls.contracts.settings);
              }}
            >
              {t("Settings")}
            </MenuItem>
          </Explanation>

          <MenuItem
            icon={<Icon as={FaTasks} w="15px" h="15px" marginBottom={-1} />}
            onClick={() => {
              navigate(urls.contracts.viewTasks);
            }}
          >
            {t("Tasks")}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};
